/**
 * SystemAlertBanner — top-of-app banner that only appears when something
 * is actually wrong. Keeps the UI quiet under normal conditions and draws
 * the operator's eye the moment a sensor drops off.
 *
 * Scope (Phase 1):
 *   • Sensor status: any sensor in OFFLINE / DEGRADED / ERROR → red banner
 *     with the count and a shortcut to /sensors.
 *
 * When the alerts API comes online (Phase 3), append a second banner row
 * for unacknowledged CRITICAL alerts — the container already supports
 * stacking multiple banner entries.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { sensorsApi } from '../../services/api';

// Status values that should trigger the banner. OFFLINE is the primary
// trigger (sensor fully down); DEGRADED / ERROR are included so partial
// failures aren't silently ignored.
const BAD_SENSOR_STATUSES = new Set(['OFFLINE', 'DEGRADED', 'ERROR']);

interface BannerEntry {
  key: string;
  label: string;
  to?: string;
  tone: 'alert' | 'warning';
}

export default function SystemAlertBanner() {
  const { data: sensorsData } = useQuery({
    queryKey: ['sensors'],
    queryFn: () => sensorsApi.list(),
    refetchInterval: 30_000, // poll every 30 s; sensor state changes are not high-frequency
  });

  const sensors = sensorsData?.data || [];
  const badSensors = sensors.filter((s: any) => BAD_SENSOR_STATUSES.has(s.status));

  const entries: BannerEntry[] = [];
  if (badSensors.length > 0) {
    const offlineCount = badSensors.filter((s: any) => s.status === 'OFFLINE').length;
    const otherCount = badSensors.length - offlineCount;
    const parts: string[] = [];
    if (offlineCount > 0) parts.push(`${offlineCount} offline`);
    if (otherCount > 0) parts.push(`${otherCount} degraded`);
    entries.push({
      key: 'bad-sensors',
      label: `Sensor issue — ${parts.join(', ')}`,
      to: '/sensors',
      tone: 'alert',
    });
  }

  if (entries.length === 0) return null;

  return (
    <div className="sticky top-14 z-30">
      {entries.map((e) => {
        const toneClass =
          e.tone === 'alert'
            ? 'bg-alert/15 border-alert/40 text-alert'
            : 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400';
        const body = (
          <div
            className={`flex items-center gap-3 border-b ${toneClass} px-4 sm:px-6 lg:px-8 py-2 text-sm`}
          >
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">{e.label}</span>
            {e.to && (
              <span className="ml-auto text-xs opacity-80">
                View {e.to.replace('/', '')} &rarr;
              </span>
            )}
          </div>
        );
        return e.to ? (
          <Link key={e.key} to={e.to} className="block hover:brightness-110 transition">
            {body}
          </Link>
        ) : (
          <div key={e.key}>{body}</div>
        );
      })}
    </div>
  );
}
